import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, resolveImg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Edit3 } from "lucide-react";
import { EntityFormDialog, TYPES } from "@/components/admin/EntityForm";
import { ReviewsTab } from "@/components/admin/ReviewsTab";
import { GuidesTab } from "@/components/admin/GuidesTab";


export default function Admin() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (ready && (!user || user.role !== "admin")) nav("/auth");
  }, [user, ready, nav]);
  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-gold-dark">Admin</div>
        <h1 className="font-display text-3xl font-bold text-indigo-deep">Control Panel</h1>
      </div>
      <Stats />
      <Tabs defaultValue="entities" className="mt-6">
        <TabsList>
          <TabsTrigger data-testid="admin-tab-entities" value="entities">Entities</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-bookings" value="bookings">Bookings</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-inquiries" value="inquiries">Inquiries</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-reviews" value="reviews">Reviews</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-guides" value="guides">Guides</TabsTrigger>
        </TabsList>
        <TabsContent value="entities"><EntitiesTab /></TabsContent>
        <TabsContent value="bookings"><BookingsTab /></TabsContent>
        <TabsContent value="inquiries"><InquiriesTab /></TabsContent>
        <TabsContent value="reviews"><ReviewsTab /></TabsContent>
        <TabsContent value="guides"><GuidesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function Stats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(r => setS(r.data)); }, []);
  if (!s) return null;
  const cards = [
    { l: "Entities", v: s.entities }, { l: "Bookings", v: s.bookings },
    { l: "Inquiries", v: s.inquiries }, { l: "Pending reviews", v: s.pending_reviews }, { l: "Users", v: s.users },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {cards.map(c => (
        <div key={c.l} className="bg-white rounded-xl p-4 border border-gold/15">
          <div className="text-xs uppercase tracking-wider text-indigo-deep/50">{c.l}</div>
          <div className="font-display text-2xl font-bold text-indigo-deep">{c.v}</div>
        </div>
      ))}
    </div>
  );
}

function EntitiesTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => api.get("/admin/entities", { params: filter === "all" ? {} : { type: filter } }).then(r => setItems(r.data)), [filter]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this entity?")) return;
    await api.delete(`/admin/entities/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between gap-3 mb-4">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="admin-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="admin-new-entity" onClick={() => setEditing({ type: "package", published: true, is_bookable: true })} className="bg-gold text-indigo-deep hover:bg-gold-dark hover:text-white"><Plus className="w-4 h-4 mr-1" />New</Button>
          </DialogTrigger>
          <EntityFormDialog editing={editing} onClose={() => { setOpen(false); setEditing(null); load(); }} />
        </Dialog>
      </div>
      <div className="bg-white rounded-xl border border-gold/15 divide-y">
        {items.map(e => (
          <div key={e.id} data-testid={`admin-row-${e.slug}`} className="p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <img src={resolveImg(e.image)} alt="" className="w-12 h-12 rounded object-cover" onError={(ev) => ev.currentTarget.classList.add("hidden")} />
              <div className="min-w-0">
                <div className="font-semibold text-indigo-deep truncate">{e.name}</div>
                <div className="text-xs text-indigo-deep/60">{e.type} · {e.state || "-"} · /{e.slug}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {e.featured && <span className="text-[10px] font-bold uppercase bg-gold text-indigo-deep px-2 py-0.5 rounded-full">★</span>}
              {e.price && <span className="text-sm font-semibold text-saffron">₹{Number(e.price).toLocaleString("en-IN")}</span>}
              <Button size="sm" variant="ghost" onClick={() => { setEditing(e); setOpen(true); }} data-testid={`edit-${e.slug}`}><Edit3 className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => del(e.id)} data-testid={`delete-${e.slug}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center text-indigo-deep/60">No entities</div>}
      </div>
    </div>
  );
}

function BookingsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/bookings").then(r => setRows(r.data)); }, []);
  return (
    <div className="mt-4 bg-white rounded-xl border border-gold/15 divide-y">
      {rows.length === 0 && <div className="p-8 text-center text-indigo-deep/60">No bookings</div>}
      {rows.map(b => (
        <div key={b.id} className="p-3 flex justify-between gap-3 text-sm">
          <div>
            <div className="font-semibold text-indigo-deep">{b.entity_name}</div>
            <div className="text-xs text-indigo-deep/60">{b.guest_name} · {b.guest_email} · {b.guest_phone}</div>
            <div className="text-xs text-indigo-deep/50">{new Date(b.created_at).toLocaleString()}</div>
          </div>
          <div className="text-right">
            <div className="font-display font-bold text-saffron">₹{Number(b.amount || 0).toLocaleString("en-IN")}</div>
            <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">{b.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function InquiriesTab() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/admin/inquiries").then(r => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const mark = async (id, status) => { await api.patch(`/admin/inquiries/${id}`, { status }); toast.success(`Marked ${status}`); load(); };
  return (
    <div className="mt-4 bg-white rounded-xl border border-gold/15 divide-y">
      {rows.length === 0 && <div className="p-8 text-center text-indigo-deep/60">No inquiries</div>}
      {rows.map(i => (
        <div key={i.id} className="p-3 flex justify-between gap-3 text-sm">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-indigo-deep">{i.entity_name || "General"}</div>
            <div className="text-xs text-indigo-deep/60">{i.name} · {i.email} · {i.phone}</div>
            {i.message && <div className="text-xs text-indigo-deep/70 mt-1 line-clamp-2">{i.message}</div>}
            <div className="text-[11px] text-indigo-deep/50">{new Date(i.created_at).toLocaleString()}</div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${i.status === "new" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{i.status}</span>
            {i.status !== "resolved" && <Button size="sm" variant="ghost" onClick={() => mark(i.id, "resolved")} data-testid={`resolve-${i.id}`}>Resolve</Button>}
          </div>
        </div>
      ))}
    </div>
  );
}

